import React, { useState, useEffect, useRef } from "react";
import {
  FiUser,
  FiSearch,
  FiMoreHorizontal,
  FiEdit2,
  FiCheck,
  FiX,
  
} from "react-icons/fi";
import {  AlertTriangle  } from "lucide-react";

import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import useProject from "../hooks/useProject";
import useDashboard from "../hooks/useDashboard";
import kanbanService from "../services/kanbanService";
import { Icons } from "../components/icons";
import { pushNotification } from "../components/Topbar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const normalizeLists = (lists = []) =>
  lists.map((list) => ({
    ...list,
    tasks: Array.isArray(list?.tasks) ? list.tasks : [],
  }));

// Normalize a picked date to noon UTC of its calendar day so the picker's
// click-time stamp can't make a valid end date compare as before the start.
const toDateOnlyISO = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return new Date(
    Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12),
  ).toISOString();
};

const Projects = () => {
  const [selectedTaskDesc, setSelectedTaskDesc] = useState(null);

  const { currentUser } = useOutletContext();
  const [showDisabledProjects, setShowDisabledProjects] = useState(false);

  const {
    columns,
    allUsers,
    selectedProject,
    isAddingMember,
    isFetchingSummary,
    loadProjectLists,
    loadProjectSummary,
    loadProject,
    isUpdatingProject,
    updateProject,
    loadAllUsers,
    addMember,
    removeMember,
    selectProject,
    deselectProject,
    updateColumns,
    clearUsers,
    exportCSV,
    disableProject,
    enableProject,
  } = useProject();

  const {
    projects,
    disabledProjects,
    isDisabledProjectsLoading,
    loadProjects,
    loadDisabledProjects,
  } = useDashboard();

  // ── Load users for member picker on mount ────────────────────────
  useEffect(() => {
    clearUsers();
    loadAllUsers(true);
    loadProjects(); // keep projects list fresh
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch lists + summary when a project is selected ────────────
  // ── Local UI state (unchanged) ───────────────────────────────────

  const taskStartDateRef = useRef(null);
  const taskEndDateRef = useRef(null);
  const [showMenu, setShowMenu] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showResources] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [taskPage, setTaskPage] = useState(1);
  const tasksPerPage = 5;
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskFormError, setTaskFormError] = useState("");
  const [taskForm, setTaskForm] = useState({
    listId: "", // ← was listName: ""
    taskName: "",
    type: "requirements",
    priority: "high",
    startDate: "",
    endDate: "",
    assignee: "",
    hours: "",
    description: "",
  });

  const navigate = useNavigate();
  const { projectRef } = useParams();

  const projectId = projectRef?.split("-").pop();

  // Single source of truth for which view to show — computed directly from
  // the URL and Redux state on every render, so switching between projects
  // (or back to the list) never flashes stale content.
  const showProjectDashboard =
    Boolean(projectId) && selectedProject?._id === projectId;
  const showProjectList = !projectId;
  const isResolvingProject = Boolean(projectId) && !showProjectDashboard;

  // ── Disabled-project access control ───────────────────────────────
  // isProjectDisabled: true for EVERYONE when the project is disabled —
  //   drives the dimmed/locked look for the dashboard body.
  // canManage: admins/managers keep a live header ("..." Edit/Enable menu)
  //   so they can re-enable the project.
  // isBlockedByDisable: true only for non-privileged users — drives the
  //   "Contact your admin" overlay message.
  const isProjectDisabled =
    showProjectDashboard && Boolean(selectedProject?.isDisabled);
  // Normalized once so every role check below is case-insensitive — avoids
  // managers silently losing access if the backend ever sends "Manager"
  // instead of "manager".
  const userRole = currentUser?.role?.toLowerCase() || "";
  const canManage = userRole === "admin" || userRole === "manager";
  const isAdmin = userRole === "admin";
  const isBlockedByDisable = isProjectDisabled && !canManage;

  // URL drives the view: a project id in the URL opens that project;
  // no id (Back button or browser back) closes it in one click.
  useEffect(() => {
    if (projectId) {
      if (projects?.length > 0 || disabledProjects?.length > 0) {
        const project = [...projects, ...disabledProjects].find(
          (p) => p._id === projectId,
        );
        if (project) {
          selectProject(project);
          loadProject(project._id);
        }
      }
    } else {
      deselectProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projects, disabledProjects]);
  const rawStatus = String(
    selectedProject?.trackingStatus || selectedProject?.status || "",
  ).toLowerCase();
  const status =
    selectedProject?.progress === 100 || rawStatus === "completed"
      ? "completed"
      : rawStatus === "at-risk"
        ? "at-risk"
        : "on-track";
  const resources = selectedProject?.members || [];

  const filteredMembers = (allUsers || []).filter((m) =>
    m.name?.toLowerCase().includes(search.toLowerCase()),
  );
  const visibleProjects = showDisabledProjects ? disabledProjects : projects;
  // ── Task helpers (unchanged) ─────────────────────────────────────
  const allTasks = normalizeLists(columns).flatMap((col) =>
    col.tasks.map((task) => ({ ...task, listName: col.name })),
  );

  const grouped = {};
  allTasks.forEach((task) => {
    const type = task.taskType?.toLowerCase();
    if (!grouped[type]) grouped[type] = { total: 0, done: 0 };
    grouped[type].total++;
    if (task.status === "done") grouped[type].done++;
  });

  const TASK_TYPES = [
    { name: "requirements", color: "#22C55E" },
    { name: "design", color: "#4253C7" },
    { name: "frontend", color: "#4379FE" },
    { name: "backend", color: "#729BFF" },
    { name: "others", color: "#F79C32" },
    { name: "testing", color: "#E5E5E5" },
    { name: "deployment", color: "#E5E5E5" },
  ];
  const getInitials = (name = "") => name.trim().charAt(0).toUpperCase() || "?";

  const getProfileImageUrl = (member) => {
    if (!member) return "";
    if (typeof member.profilePicture === "string") return member.profilePicture;
    if (member.profilePicture?.url) return member.profilePicture.url;
    if (typeof member.avatar === "string") return member.avatar;
    if (typeof member.profileImage === "string") return member.profileImage;
    return "";
  };
  // Normalizes a project-member object: { user: {...}, role, _id }
  // into a flat { _id, name, role, profilePicture } shape.
  const getMemberInfo = (m = {}) => {
    const source = m.user && typeof m.user === "object" ? m.user : m;
    return {
      _id: m._id || source._id,
      name: source.name || m.name || "",
      role: m.role || source.role || "",
      profilePicture:
        source.profilePicture || source.avatar || source.profileImage || "",
    };
  };
  const timelineData = TASK_TYPES.map((t) => {
    const data = grouped[t.name];
    const percent = data ? Math.round((data.done / data.total) * 100) : 0;
    return { name: t.name, percent, color: t.color };
  });

  const totalTaskItems = allTasks.length;
  const taskStartIndex = (taskPage - 1) * tasksPerPage;
  const taskEndIndex = taskStartIndex + tasksPerPage;
  const currentTasks = allTasks.slice(taskStartIndex, taskEndIndex);
  const totalTaskPages = Math.ceil(totalTaskItems / tasksPerPage);

  useEffect(() => {
    if (!selectedProject) return;
    loadProjectLists(selectedProject._id);
    loadProjectSummary(selectedProject._id);
  }, [selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProjectTaskCount = (p) =>
    p.taskCount ??
    p.totalTasks ??
    (p.lists || []).reduce((sum, l) => sum + (l?.tasks?.length || 0), 0);
  const getStatusStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed") return "bg-[#D8F5D9] text-[#1D7A31]";
    if (s === "at-risk") return "bg-[#FFD6D6] text-[#D60000]";
    if (s === "on-track") return "bg-[#CDDDFA] text-[#4379FE]";
    return "bg-[#E5E7EB] text-black/60";
  };

  const getTimeAgo = (date) => {
    if (!date) return "Just now";
    const diff = Math.floor((new Date() - new Date(date)) / 1000);
    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(diff / 3600);
    const days = Math.floor(diff / 86400);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // ── Create task (replaces authFetch inline) ───────────────────────
  const handleCreateTask = async () => {
    // All Add Task fields are mandatory — errors show inside the card
    const err = (() => {
      if (!taskForm.listId) return "*List name is required.";
      if (!taskForm.taskName.trim()) return "*Task name is required.";
      if (!taskForm.type) return "*Task type is required.";
      if (!taskForm.priority) return "*Priority is required.";
      if (!taskForm.startDate) return "*Start date is required.";
      if (!taskForm.endDate) return "*End date is required.";
      if (!taskForm.assignees || taskForm.assignees.length === 0)
        return "*At least one task assignee is required.";
      if (!taskForm.hours || parseInt(taskForm.hours) <= 0)
        return "*Estimated hours is required.";
      if (!taskForm.description.trim()) return "*Description is required.";
      if (!selectedProject) return "*Select project";

      // Prevent duplicate task names within the project (case-insensitive)
      const nameToCheck = taskForm.taskName.trim().toLowerCase();
      const isDuplicate = allTasks.some(
        (t) => (t.name || "").trim().toLowerCase() === nameToCheck,
      );
      if (isDuplicate) return "*A task with this name already exists.";

      return "";
    })();
    if (err) {
      setTaskFormError(err);
      return;
    }
    setTaskFormError("");

    try {
      const data = await kanbanService.createTask(
        selectedProject._id,
        taskForm.listId, // ← was: list._id after find()
        {
          name: taskForm.taskName,
          taskType: taskForm.type,
          assignees: taskForm.assignees || [],
          estimatedHours: taskForm.hours ? parseInt(taskForm.hours) : 0,
          taskStartDate: toDateOnlyISO(taskForm.startDate),
          taskDeadline: toDateOnlyISO(taskForm.endDate),
          description: taskForm.description,
          priority: taskForm.priority.toLowerCase(),
        },
      );
      pushNotification({
        title: "Task created successfully",
        lines: [`Task: ${taskForm.taskName}`],
      });
      updateColumns(
        columns.map((col) =>
          col._id === taskForm.listId
            ? { ...col, tasks: [...col.tasks, data.data] }
            : col,
        ),
      );
      setTaskForm({
        listId: "",
        taskName: "",
        type: "requirements",
        priority: "high",
        startDate: "",
        endDate: "",
        assignees: [],
        hours: "",
        description: "",
      });
      setSelectedAssignees([]);
      setAssigneeSearch("");
      setShowTaskModal(false);
    } catch (err) {
      setTaskFormError(
        err?.response?.data?.message || "*Failed to create task.",
      );
    }
  };
  const handleExportCSV = () => {
    if (!selectedProject?._id) return;
    exportCSV(selectedProject._id);
  };

  // Holds the member pending removal — drives the confirmation popup
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);
  const [cannotRemoveAdmin, setCannotRemoveAdmin] = useState(null);

const handleRemoveMember = async (memberId) => {
    if (!selectedProject?._id || !memberId) return;
    try {
      await removeMember(selectedProject._id, memberId);
      await loadProjectSummary(selectedProject._id);
      setShowMenu(null);
      setConfirmRemoveMember(null);
      pushNotification({
        title: "Member removed successfully",
        lines: [],
      });
    } catch (err) {
      setConfirmRemoveMember(null);
      alert(err?.response?.data?.message || "Failed to remove member.");
    }
  };

  const handleAddMembers = async () => {
    if (!selectedProject?._id) return;

    const existingMemberIds = new Set(resources.map((member) => member._id));
    const membersToAdd = selectedMembers.filter(
      (memberId) => !existingMemberIds.has(memberId),
    );

    if (membersToAdd.length === 0) {
      setShowModal(false);
      return;
    }

    try {
      for (const memberId of membersToAdd) {
        await addMember(selectedProject._id, memberId);
      }
      await loadProjectSummary(selectedProject._id);
      setSelectedMembers([]);
      setSearch("");
      setShowModal(false);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to add member.");
    }
  };
  // ── Inline edit: project name/description ──
  const startEditProjectInfo = () => {
    setEditName(selectedProject?.name || "");
    setEditDescription(selectedProject?.description || "");
    setIsEditingProjectInfo(true);
  };

  const cancelEditProjectInfo = () => {
    setIsEditingProjectInfo(false);
  };

  const saveEditProjectInfo = async () => {
    if (!selectedProject?._id) return;
    try {
      await updateProject(selectedProject._id, {
        name: editName,
        description: editDescription,
      });
      await loadProject(selectedProject._id);
      await loadProjects();
      setIsEditingProjectInfo(false);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save project changes.");
    }
  };

  const handleToggleDisable = async () => {
    if (!selectedProject?._id) return;
    try {
      if (selectedProject.isDisabled) {
        await enableProject(selectedProject._id).unwrap();
      } else {
        await disableProject(selectedProject._id).unwrap();
      }
      await loadProject(selectedProject._id);
      await loadProjects();
      await loadDisabledProjects();
      setShowDisableConfirm(false);
      setShowHeaderMenu(false);
    } catch (err) {
      alert(
        typeof err === "string"
          ? err
          : err?.response?.data?.message || "Unable to update project status.",
      );
    }
  };
  // ── Inline edit: start date / deadline ──
  const startEditDates = () => {
    setEditStartDate(
      selectedProject?.startDate ? new Date(selectedProject.startDate) : null,
    );
    setEditDeadline(
      selectedProject?.deadline ? new Date(selectedProject.deadline) : null,
    );
    setEditPriority(selectedProject?.priority || "high");
    setIsEditingDates(true);
  };

  const cancelEditDates = () => {
    setIsEditingDates(false);
  };

  const saveEditDates = async () => {
    if (!selectedProject?._id) return;
    try {
      await updateProject(selectedProject._id, {
        startDate: editStartDate,
        deadline: editDeadline,
        priority: editPriority,
      });
      await loadProject(selectedProject._id);
      await loadProjects();
      setIsEditingDates(false);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save project dates.");
    }
  };
  // ═══════════════════════════════════════════════════════════════════
  // JSX
  // ═══════════════════════════════════════════════════════════════════
  const getPriorityBadge = (priority) => {
    const p = (priority || "").toUpperCase();
    if (p === "LOW") return "bg-[#CFFFDE] text-[#003C18]";
    if (p === "MEDIUM") return "bg-[#FFE8B6] text-[#B76E00]";
    if (p === "HIGH") return "bg-[#FFCA75CC] text-[#FF1A00]";
    if (p === "CRITICAL") return "bg-[#FFD6D6] text-[#D60000]";
    return "bg-blue-100 text-blue-700";
  };

  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const assigneeDropdownRef = useRef(null);
  const [openResourceRow, setOpenResourceRow] = useState(null);
  const resourcePopupRef = useRef(null);
  const [showManagerBlocked, setShowManagerBlocked] = useState(false);
  const removeMenuRef = useRef(null);
  const taskDescPopupRef = useRef(null);
  // Ref for the "Project Information" card — used to detect outside
  // clicks while the start-date/deadline/priority fields are in edit mode.
  const projectInfoCardRef = useRef(null);


  useEffect(() => {
    const handleClickOutsideResource = (e) => {
      if (
        resourcePopupRef.current &&
        !resourcePopupRef.current.contains(e.target)
      ) {
        setOpenResourceRow(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideResource);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideResource);
  }, []);

  useEffect(() => {
    const handleClickOutsideHeaderMenu = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
        setShowHeaderMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideHeaderMenu);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideHeaderMenu);
  }, []);
  useEffect(() => {
  const handleClickOutsideRemoveMenu = (e) => {
    if (removeMenuRef.current && !removeMenuRef.current.contains(e.target)) {
      setShowMenu(null);
    }
  };
  document.addEventListener("mousedown", handleClickOutsideRemoveMenu);
  return () =>
    document.removeEventListener("mousedown", handleClickOutsideRemoveMenu);
}, []);


useEffect(() => {
  const handleClickOutsideTaskDesc = (e) => {
    if (
      taskDescPopupRef.current &&
      !taskDescPopupRef.current.contains(e.target)
    ) {
      setSelectedTaskDesc(null);
    }
  };
  document.addEventListener("mousedown", handleClickOutsideTaskDesc);
  return () =>
    document.removeEventListener("mousedown", handleClickOutsideTaskDesc);
}, []);

  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const typeDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);

  const TASK_TYPE_OPTIONS = [
    "requirements",
    "design",
    "others",
    "frontend",
    "backend",
    "testing",
    "deployment",
  ];
  const PRIORITY_OPTIONS = ["high", "medium", "low", "critical"];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(e.target)
      ) {
        setShowAssigneeDropdown(false);
      }
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(e.target)
      ) {
        setShowTypeDropdown(false);
      }
      if (
        priorityDropdownRef.current &&
        !priorityDropdownRef.current.contains(e.target)
      ) {
        setShowPriorityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // (Modal edit handlers removed — inline editing is used elsewhere)

  // Inline edit: project name + description
  const [isEditingProjectInfo, setIsEditingProjectInfo] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const headerMenuRef = useRef(null);
  // Inline edit: start date + deadline + priority
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editStartDate, setEditStartDate] = useState(null);
  const [editDeadline, setEditDeadline] = useState(null);
  const [editPriority, setEditPriority] = useState("high");

  // Close the Project Information edit fields (start date / deadline /
  // priority) when clicking anywhere outside that card. This only cancels
  // the edit (like the ✗ button) — it does not save changes.
  useEffect(() => {
    const handleClickOutsideProjectInfo = (e) => {
      if (
        isEditingDates &&
        projectInfoCardRef.current &&
        !projectInfoCardRef.current.contains(e.target)
      ) {
        setIsEditingDates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideProjectInfo);
    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutsideProjectInfo,
      );
  }, [isEditingDates]);

  // (Pagination component and modal save handler removed — inline controls are used)

  return (
    <div
      className="min-h-screen font-[Poppins] bg-transparent px-4 md:px-6 py-5"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {/* ── ADD TASK MODAL ── */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3 py-4">
          <div
            className="bg-white w-full max-w-[780px] rounded-2xl shadow-xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ padding: "30px" }}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold">Add Task</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Define tasks for your project workflow.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  setTaskFormError("");
                }}
                className="text-gray-400 hover:text-gray-600 text-lg"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[16px] font-normal text-black">
                  List Name
                </label>
                <div className="relative mt-1">
                  <select
                    className="w-full h-[40px] border border-gray-300 rounded-lg pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none bg-white"
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, listId: e.target.value })
                    }
                  >
                    <option value="">Select a list</option>
                    {columns.map((col) => (
                      <option key={col._id} value={col._id}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[16px] font-normal text-black">
                  Task Name
                </label>
                <input
                  className="w-full mt-1 h-[40px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Enter your task name"
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, taskName: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-[16px] font-normal text-black">
                  Task Type
                </label>
                <div className="relative mt-1" ref={typeDropdownRef}>
                  <div
                    className="w-full h-[40px] border border-gray-300 rounded-lg pl-3 pr-8 text-sm flex items-center cursor-pointer bg-white"
                    onClick={() => setShowTypeDropdown((prev) => !prev)}
                  >
                    <span className="capitalize text-black">{taskForm.type}</span>
                  </div>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${
                        showTypeDropdown ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {showTypeDropdown && (
                    <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                      {TASK_TYPE_OPTIONS.map((option) => (
                        <div
                          key={option}
                          className="px-3 py-2.5 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setTaskForm({ ...taskForm, type: option });
                            setShowTypeDropdown(false);
                          }}
                        >
                          <span className="text-sm font-medium text-black capitalize">{option}</span>
                          {taskForm.type === option && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[16px] font-normal text-black">Priority</label>
                <div className="relative mt-1" ref={priorityDropdownRef}>
                  <div className="w-full h-[40px] border border-gray-300 rounded-lg pl-3 pr-8 text-sm flex items-center cursor-pointer bg-white" onClick={() => setShowPriorityDropdown((prev) => !prev)}>
                    <span className="capitalize text-black">{taskForm.priority}</span>
                  </div>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg className={`w-4 h-4 text-gray-500 transition-transform ${showPriorityDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {showPriorityDropdown && (
                    <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                      {PRIORITY_OPTIONS.map((option) => (
                        <div key={option} className="px-3 py-2.5 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0" onClick={() => { setTaskForm({ ...taskForm, priority: option }); setShowPriorityDropdown(false); }}>
                          <span className="text-sm font-medium text-black capitalize">{option}</span>
                          {taskForm.priority === option && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">Start date</label>
                <div className="relative mt-1 w-full">
                  <div className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10" onClick={() => taskStartDateRef.current.setFocus()}>
                    <Icons.Calendar />
                  </div>
                  <DatePicker ref={taskStartDateRef} selected={taskForm.startDate ? new Date(taskForm.startDate) : null} onChange={(date) => setTaskForm({ ...taskForm, startDate: date })} placeholderText="Select start date" className="w-full h-[40px] border border-gray-300 rounded-[8px] pl-[50px] pr-3 text-sm" wrapperClassName="w-full block" dateFormat="dd MMM yyyy" />
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">End date</label>
                <div className="relative mt-1 w-full">
                  <div className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10" onClick={() => taskEndDateRef.current.setFocus()}>
                    <Icons.Calendar />
                  </div>
                  <DatePicker ref={taskEndDateRef} selected={taskForm.endDate ? new Date(taskForm.endDate) : null} onChange={(date) => setTaskForm({ ...taskForm, endDate: date })} placeholderText="Select end date" className="w-full h-[40px] border border-gray-300 rounded-[8px] pl-[50px] pr-3 text-sm" wrapperClassName="w-full block" dateFormat="dd MMM yyyy" minDate={taskForm.startDate || null} />
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">Task Assignee</label>
                <div className="relative mt-1" ref={assigneeDropdownRef}>
                  <div className="absolute left-3 top-[20px] -translate-y-1/2 text-gray-400 z-10"><Icons.Search className="w-4 h-4" /></div>
                  <input type="text" placeholder="Search Members" className="w-full h-[40px] border border-gray-300 rounded-lg pl-9 pr-3 text-sm" value={assigneeSearch} onClick={() => setShowAssigneeDropdown(true)} onChange={(e) => { setAssigneeSearch(e.target.value); setShowAssigneeDropdown(true); }} />
                  {showAssigneeDropdown && (
                    <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                      {resources.filter((m) => m.name?.toLowerCase().includes(assigneeSearch.toLowerCase())).map((member) => {
                        const isSelected = selectedAssignees.some((m) => m._id === member._id);
                        return (
                          <div key={member._id} className="px-3 py-2.5 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100" onClick={() => {
                            setSelectedAssignees((prev) => isSelected ? prev.filter((m) => m._id !== member._id) : [...prev, member]);
                            setTaskForm({ ...taskForm, assignees: isSelected ? taskForm.assignees.filter((id) => id !== member._id) : [...(taskForm.assignees || []), member._id] });
                          }}>
                            <div className="flex items-center gap-2"><div className="w-[24px] h-[24px] rounded-full border border-gray-400 flex items-center justify-center"><Icons.User /></div><p className="text-sm font-medium text-black">{member.name}</p></div>
                            <div className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center ${isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"}`}>{isSelected && (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>)}</div>
                          </div>
                        );
                      })}
                      {resources.filter((m) => m.name?.toLowerCase().includes(assigneeSearch.toLowerCase())).length === 0 && (<div className="px-3 py-3 text-gray-400 text-sm">No members found</div>)}
                    </div>
                  )}
                  {selectedAssignees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">{selectedAssignees.map((member) => (<div key={member._id} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"><span>{member.name}</span><button type="button" onClick={() => { setSelectedAssignees((prev) => prev.filter((m) => m._id !== member._id)); setTaskForm({ ...taskForm, assignees: taskForm.assignees.filter((id) => id !== member._id) }); }}>✕</button></div>))}</div>)}
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">Estimated Hours</label>
                <input className="w-full mt-1 h-[40px] border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Hours" onChange={(e) => setTaskForm({ ...taskForm, hours: e.target.value })} />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="text-[16px] font-normal text-black">Description</label>
                <textarea rows="3" className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Enter task description" onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} />
              </div>
            </div>
            {taskFormError && (<div className="mt-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">{taskFormError}</div>)}
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowTaskModal(false); setTaskFormError(""); }} className="px-5 py-2 w-[163px] border rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleCreateTask} className="px-5 py-2 w-[163px] bg-[#4253C7] hover:bg-[#2f63e0] text-white rounded-lg">Create Task</button>
            </div>
          </div>
        </div>
      )}

      {isResolvingProject && (
        <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
          Loading project...
        </div>
      )}

      {/* ── PROJECT LIST ── */}
      {showProjectList && (
        <>
          {/* Title */}
          {/* Title */}
<div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
  <div>
    <h1 className="text-[24px] md:text-[32px] font-semibold text-black">
      Projects
    </h1>
    <p className="text-[14px] text-[#7E7E7E] mt-1">
      Manage And Track All Your Projects In One Piece
    </p>
  </div>

  {isAdmin && (
  <button
    onClick={() => {
      if (!showDisabledProjects) loadDisabledProjects();
      setShowDisabledProjects((prev) => !prev);
    }}
    className={
      showDisabledProjects
        ? "w-[50px] h-[37px] p-[8px] flex items-center justify-center gap-[10px] rounded-[8px] text-[#4253C7] text-[14px] leading-[21px] font-normal underline capitalize hover:text-[#2f3fa8] transition whitespace-nowrap"
        : "px-[16px] py-[8px] h-[37px] border border-[#4253C7] rounded-[8px] text-[#4253C7] text-[14px] font-normal hover:bg-blue-50 transition whitespace-nowrap"
    }
  >
    {showDisabledProjects ? "Back" : "View Disable Projects"}
  </button>
  )}
</div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
  {isDisabledProjectsLoading && showDisabledProjects ? (
    <p className="text-gray-400">Loading disabled projects...</p>
  ) : visibleProjects.length === 0 ? (
    <p className="text-gray-400">
      {showDisabledProjects
        ? "No disabled projects 🚀"
        : "No Projects Found 🚀"}
    </p>
  ) : (
    visibleProjects.map((p, i) => {
const status = p.trackingStatus || p.status || p.projectStatus;                return (
                  <div
                    key={p._id || i}
                    className="
  bg-white
  rounded-[16px]
  min-h-[237px]
  px-5
  py-5
  shadow-sm
"
                  >
                    {/* Header */}
                    <div className="flex gap-4">
                      {/* Icon Box */}
                      <div
                        className="
      w-[52px]
      h-[52px]
      rounded-[16px]
      bg-[#E7EEFF]
      flex
      items-center
      justify-center
    "
                      >
                        <Icons.KanbanPic />
                      </div>

                      {/* Project Name & Status */}
                      <div>
                        <h2 className="text-[18px] md:text-[22px] font-medium">
                          Project {p.name}
                        </h2>

                        <span
                          className={`mt-2 inline-flex px-3 h-[24px] items-center rounded-full text-[11px] font-medium ${
                            p.isDisabled
                              ? "bg-[#E5E7EB] text-black/50"
                              : getStatusStyle(status)
                          }`}
                        >
                          {p.isDisabled
                            ? "DISABLED"
                            : status
                              ? status.toUpperCase()
                              : "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-b mt-4 mb-4"></div>

                    {/* Statistics */}
                    <div className="flex flex-col sm:flex-row gap-4 sm:justify-between">
                      {/* Tasks */}
                      <div className="flex gap-2">
                        <div
                          className="
        w-[48px]
        h-[48px]
        bg-[#EDF3FF]
        rounded-[8px]
        flex
        items-center
        justify-center
      "
                        >
                          <Icons.KanbanClip />
                        </div>

                        <div>
                          <p className="text-[14px] font-medium">
                            {getProjectTaskCount(p)}
                          </p>

                          <p className="text-[10px] text-gray-400">tasks</p>
                        </div>
                      </div>

                      {/* Members */}
                      <div className="flex gap-2">
                        <div
                          className="
        w-[48px]
        h-[48px]
        bg-[#F3ECFF]
        rounded-[8px]
        flex
        items-center
        justify-center
      "
                        >
                          <Icons.KanbanTeam />
                        </div>

                        <div>
                          <p className="text-[14px] font-medium">
                            {p.totalMembers ?? p.members?.length ?? 0}
                          </p>

                          <p className="text-[10px] text-gray-400">
                            team members
                          </p>
                        </div>
                      </div>

                      {/* Last updated */}
                      <div className="flex gap-2">
                        <div
                          className="
        w-[48px]
        h-[48px]
        bg-[#FFF3E6]
        rounded-[8px]
        flex
        items-center
        justify-center
      "
                        >
                          <Icons.D />
                        </div>

                        <div>
                          <p className="text-[14px] font-medium">
                            {getTimeAgo(p.updatedAt)}
                          </p>

                          <p className="text-[10px] text-gray-400">
                            last updated
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Button */}
                    <div className="flex justify-end mt-5">
                      <button
                        onClick={() => {
                          selectProject(p);
                          navigate(`/dashboard/projects/${p._id}`);
                        }}
                        className="
      w-[126px]
      h-[35px]
      bg-[#4253C7]
      rounded-[8px]
      text-white
      text-[13px]
    "
                      >
                        Open Project →
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* ── PROJECT DASHBOARD ── */}
      {showProjectDashboard && (
        <>
          <button
            onClick={() => {
              deselectProject();
              navigate("/dashboard/projects");
            }}
            className="mb-4 text-blue-500 text-sm hover:underline"
          >
            Back
          </button>

          {/* ── HEADER CARD (never dimmed — keeps Edit/Enable menu usable for admins/managers) ── */}
          <div className="bg-white rounded-[8px] shadow-[0px_0px_4px_rgba(0,0,0,0.05)] px-4 sm:px-6 py-4 sm:py-6 flex flex-col lg:flex-row justify-between lg:items-start gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <span
                  className={`text-[12px] px-[10px] py-[4px] rounded-full font-normal ${
                    selectedProject?.isDisabled
                      ? "bg-[#E5E7EB] text-black/50"
                      : getStatusStyle(status)
                  }`}
                >
                  {selectedProject?.isDisabled
                    ? "Disabled"
                    : status
                      ? status
                          .split("-")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join("-")
                      : "N/A"}
                </span>
                <span className="text-[12px] text-black/60 font-normal">
                  Updated {getTimeAgo(selectedProject?.updatedAt)}
                </span>
              </div>
              {isEditingProjectInfo ? (
                <div className="space-y-2 max-w-[500px]">
                  <input
                    className="w-full text-[18px] sm:text-[24px] font-medium border border-gray-300 rounded-lg px-2 py-1"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <textarea
                    rows="2"
                    className="w-full text-[12px] border border-gray-300 rounded-lg px-2 py-1"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={saveEditProjectInfo}
                      disabled={isUpdatingProject}
                      className="w-[28px] h-[28px] flex items-center justify-center rounded bg-[#4253C7] text-white"
                    >
                      <FiCheck size={14} />
                    </button>
                    <button
                      onClick={cancelEditProjectInfo}
                      className="w-[28px] h-[28px] flex items-center justify-center rounded border border-gray-300 text-gray-500"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2
                    className={`text-[18px] sm:text-[24px] font-medium leading-[36px] text-black flex items-center gap-2 w-fit ${
                      selectedProject?.isDisabled
                        ? "bg-[#F5F5F5] rounded-[8px] px-2"
                        : ""
                    }`}
                  >
                    <span className="truncate">
                      {selectedProject?.name || "Untitled Project"}
                    </span>
                    {canManage && (
                      <div className="relative" ref={headerMenuRef}>
                        <button
                          onClick={() => setShowHeaderMenu((prev) => !prev)}
                          className="w-[20px] h-[20px] rounded flex items-center justify-center hover:bg-black/10"
                        >
                          <FiMoreHorizontal className="text-black" size={16} />
                        </button>
                        {showHeaderMenu && (
                          <div className="absolute left-0 top-[26px] w-[80px] bg-white shadow-[0px_4px_12px_rgba(0,0,0,0.08)] rounded-[4px] py-1 z-30">
                            <button
                              onClick={() => {
                                startEditProjectInfo();
                                setShowHeaderMenu(false);
                              }}
                              className="w-full text-left px-2 py-1 text-[12px] text-black hover:bg-gray-50"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                if (!isAdmin) {
                                  setShowHeaderMenu(false);
                                  setShowManagerBlocked(true);
                                  return;
                                }
                                if (selectedProject?.isDisabled) {
                                  handleToggleDisable();
                                } else {
                                  setShowDisableConfirm(true);
                                  setShowHeaderMenu(false);
                                }
                              }}
                              className={`w-full text-left px-2 py-1 text-[12px] hover:bg-gray-50 ${
                                selectedProject?.isDisabled
                                  ? "text-[#4253C7]"
                                  : "text-[#FF0000]"
                              }`}
                            >
                              {selectedProject?.isDisabled
                                ? "Enable"
                                : "Disable"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </h2>
                  <p className="text-[12px] leading-[18px] text-black/50 mt-1 max-w-[500px]">
                    {selectedProject?.description || "No description available"}
                  </p>
                </>
              )}
            </div>
            <div className="flex flex-col items-start lg:items-end w-full lg:w-auto mt-4 lg:mt-0">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-medium">
                {selectedProject?.progress || 0}%
              </h2>
              <span className="text-[14px] text-black/50 font-normal tracking-wide mb-3">
                COMPLETION
              </span>
              <div className="w-[260px] sm:w-[340px] lg:w-[426px] h-[8px] bg-[rgba(198,198,198,0.36)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#528CF9] rounded-full transition-all duration-500"
                  style={{ width: `${selectedProject?.progress || 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* ── EVERYTHING BELOW THE HEADER IS LOCKED WHEN THE PROJECT IS DISABLED ── */}
          <div className="relative">
            {isBlockedByDisable && (
              <div className="absolute inset-0 z-40 flex items-start justify-center pt-24 pointer-events-none">
                <div className="bg-white rounded-[8px] shadow-lg px-8 py-6 text-center pointer-events-auto">
                  <p className="text-[18px] font-medium text-black mb-1">
                    This project has been disabled
                  </p>
                  <p className="text-[14px] text-black/50">
                    Contact your admin to regain access.
                  </p>
                </div>
              </div>
            )}
            <div
              className={
                isProjectDisabled
                  ? "opacity-50 pointer-events-none select-none"
                  : ""
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[minmax(0,1fr)_400px] gap-4 items-start">
                {" "}
                {/* ════ LEFT ════ */}
                <div className="space-y-4 min-w-0 w-full overflow-hidden">
                  {/* 3 Stat cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Members */}
                    <div className="bg-white rounded-[8px] w-full h-[125px] px-6 py-5 shadow-[0_0_4px_rgba(0,0,0,0.05)]">
                      <div className="flex items-center gap-3">
                        <div className="w-[38px] h-[39px] bg-black/10 rounded-[4px] flex items-center justify-center">
                          <Icons.Members />
                        </div>
                        <span className="text-[16px] font-normal text-black">
                          Members
                        </span>
                      </div>
                      <p className="text-[16px] lg:text-[18px] font-medium mt-5 whitespace-nowrap">
                        {resources.length} Resources
                      </p>
                    </div>
                    {/* Created By */}
                    <div className="bg-white rounded-[8px] w-full h-[125px] px-6 py-5 shadow-[0_0_4px_rgba(0,0,0,0.05)]">
                      <div className="flex items-center gap-3">
                        <div className="w-[38px] h-[39px] bg-black/10 rounded-[4px] flex items-center justify-center">
                          <Icons.User />
                        </div>
                        <span className="text-[16px] font-normal text-black">
                          Created By
                        </span>
                      </div>
                      <p className="text-[18px] font-medium mt-5 truncate">
                        {selectedProject?.createdBy?.name || "N/A"}
                      </p>
                    </div>
                    {/* Progress */}
                    <div className="bg-white rounded-[8px] w-full h-[125px] px-6 py-5 shadow-[0_0_4px_rgba(0,0,0,0.05)]">
                      <div className="flex items-center gap-3">
                        <div className="w-[38px] h-[39px] bg-black/10 rounded-[4px] flex items-center justify-center">
                          <Icons.Progress />
                        </div>
                        <span className="text-[16px] font-normal text-black">
                          Progress
                        </span>
                      </div>
                      <p className="text-[18px] font-medium mt-5">
                        {selectedProject?.progress || 0}%
                      </p>
                    </div>
                  </div>

                  {/* ── BIG TIMELINE CARD (matches design image) ── */}
                  <div className="bg-white rounded-[8px] shadow-[0_0_4px_rgba(0,0,0,0.05)] px-4 sm:px-8 pt-6 sm:pt-8 pb-8 sm:pb-10 w-full">
                    {" "}
                    {/* Timeline title */}
                    <div className="mb-11">
                      <h3 className="text-[24px] font-medium leading-[36px] text-black">
                        Project Timeline
                      </h3>
                      <p className="text-[12px] font-normal leading-[18px] text-black/50">
                        Overall Project Progress
                      </p>
                    </div>
                    {/* Progress rows — big bars like the design */}
                    <div className="space-y-8">
                      {timelineData.map((item, i) => (
                        <div key={i} className="flex items-center gap-4">
                          {/* Label — fixed 130px */}
                          <span
                            className="text-[11px] sm:text-[14px] lg:text-[16px] font-normal leading-[24px] text-black capitalize shrink-0"
                            style={{ width: "160px" }}
                          >
                            {item.name === "frontend"
                              ? "Front-End"
                              : item.name === "backend"
                                ? "Back-End"
                                : item.name === "others"
                                  ? "Development"
                                  : item.name.charAt(0).toUpperCase() +
                                    item.name.slice(1)}
                          </span>
                          {/* Percent — fixed 48px right-aligned */}
                          <span
                            className="text-[11px] sm:text-[14px] lg:text-[16px] font-normal leading-[24px] text-black shrink-0 text-right"
                            style={{ width: "36px" }}
                          >
                            {item.percent}%
                          </span>
                          {/* Bar track */}
                          <div
                            className="flex-1 rounded-[68px] overflow-hidden bg-[#C6C6C65C]"
                            style={{ height: "24px" }}
                          >
                            {item.percent > 0 && (
                              <div
                                className="h-full rounded-[68px] transition-all duration-700"
                                style={{
                                  width: `${item.percent}%`,
                                  backgroundColor: item.color,
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* ── END TIMELINE ── */}
                </div>
                {/* ════ END LEFT ════ */}
                <div className="lg:col-span-1 space-y-3 self-start">
                  <div
                    ref={projectInfoCardRef}
                    className="bg-white p-4 sm:p-6 rounded-[8px] shadow-[0px_0px_4px_rgba(0,0,0,0.05)] w-full"
                  >
                    <div className="flex items-center justify-between mb-8">
                      <h3 className="text-[24px] font-medium">
                        Project Information
                      </h3>
                      {canManage &&
                        (!isEditingDates ? (
                          <button onClick={startEditDates} className="text-black">
                            <FiEdit2 size={18} />
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={saveEditDates}
                              disabled={isUpdatingProject}
                              className="w-[26px] h-[26px] flex items-center justify-center rounded bg-[#4253C7] text-white"
                            >
                              <FiCheck size={13} />
                            </button>
                            <button
                              onClick={cancelEditDates}
                              className="w-[26px] h-[26px] flex items-center justify-center rounded border border-gray-300 text-gray-500"
                            >
                              <FiX size={13} />
                            </button>
                          </div>
                        ))}
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Start Date
                        </p>
                        {isEditingDates ? (
                         <DatePicker
  selected={editStartDate}
  onChange={(date) => setEditStartDate(date)}
  className="w-[140px] h-[32px] border border-gray-300 rounded-lg px-2 text-[13px]"
  dateFormat="dd MMM yyyy"
  placeholderText="Select date"
  portalId="datepicker-portal"
  popperPlacement="bottom-end"
/>
                        ) : (
                          <p className="text-[13px] sm:text-[16px] text-black/60">
                            {selectedProject?.startDate
                              ? new Date(
                                  selectedProject.startDate,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "N/A"}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-between items-center">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Deadline
                        </p>
                        {isEditingDates ? (
                       <DatePicker
  selected={editDeadline}
  onChange={(date) => setEditDeadline(date)}
  className="w-[140px] h-[32px] border border-gray-300 rounded-lg px-2 text-[13px]"
  dateFormat="dd MMM yyyy"
  placeholderText="Select date"
  minDate={editStartDate || null}
  portalId="datepicker-portal"
  popperPlacement="bottom-end"
/>
                        ) : (
                          <p className="text-[13px] sm:text-[16px] text-black/60">
                            {selectedProject?.deadline
                              ? new Date(
                                  selectedProject.deadline,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "N/A"}
                          </p>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Priority
                        </p>
                        {isEditingDates ? (
                          <select
                            value={editPriority}
                            onChange={(e) => setEditPriority(e.target.value)}
                            className="w-[140px] h-[32px] border border-gray-300 rounded-lg px-2 text-[13px]"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                        ) : (
                          <span
                            className={`px-3 py-1 text-[12px] font-normal rounded-full ${getPriorityBadge(selectedProject?.priority)}`}
                          >
                            {selectedProject?.priority?.toUpperCase() || "N/A"}
                          </span>
                        )}
                      </div>

                      {/* Status */}
                      <div className="flex justify-between items-center">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Status
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="w-[8px] h-[8px] bg-[#528CF9] rounded-full" />
                        <span className="text-[13px] sm:text-[16px] text-black/60 capitalize">
  {selectedProject?.trackingStatus || "N/A"}
</span>
                        </div>
                      </div>

                      {/* Created By */}
                      <div className="flex justify-between items-center">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Created By
                        </p>
                        <p className="text-[13px] sm:text-[16px] text-black/60 capitalize">
                          {selectedProject?.createdBy?.name || "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Created At
                        </p>
                        <p className="text-[14px] sm:text-[16px] text-black/60 text-right break-words max-w-[200px]">
                          {selectedProject?.createdAt
                            ? `${new Date(selectedProject.createdAt).getDate()} ${new Date(selectedProject.createdAt).toLocaleString("en-US", { month: "short" })} , ${new Date(selectedProject.createdAt).getFullYear()}-${new Date(selectedProject.createdAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                            : "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-[13px] sm:text-[16px] font-medium text-black">
                          Updated At
                        </p>
                        <p className="text-[14px] sm:text-[16px] text-black/60 text-right break-words max-w-[200px]">
                          {" "}
                          {selectedProject?.updatedAt
                            ? `${new Date(selectedProject.updatedAt).getDate()} ${new Date(selectedProject.updatedAt).toLocaleString("en-US", { month: "short" })} , ${new Date(selectedProject.updatedAt).getFullYear()}-${new Date(selectedProject.updatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resources */}
                  {showResources && (
                    <div className="bg-white rounded-[8px] shadow-[0_0_4px_rgba(0,0,0,0.05)] px-4 sm:px-6 pt-4 sm:pt-6 pb-4 w-full lg:w-[full] lg:h-[300px] flex flex-col">
                      {" "}
                      <div className="flex justify-between items-center mb-5">
                        <h3 className="text-[24px] font-medium leading-[36px] text-black">
                          Resources
                        </h3>
                        {canManage && (
                          <button
                            onClick={() => {
                              setShowModal(true);
                              setSelectedMembers(resources.map((r) => r._id));
                            }}
                            className="text-[#4253C7] text-[14px] font-normal leading-[21px] underline"
                          >
                            Add Member
                          </button>
                        )}
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {isFetchingSummary ? (
                          <div className="py-10 text-center text-sm text-gray-500">
                            Loading resources...
                          </div>
                        ) : resources.length === 0 ? (
                          <div className="py-10 text-center text-sm text-gray-500">
                            No resources assigned to this project.
                          </div>
                        ) : (
                       resources.map((raw, i) => {
  const item = getMemberInfo(raw);
  // Protected resource roles: these members can never be removed from a
  // project (admin/owner is mandatory, and managers are also protected).
  // If your backend uses different labels for these roles, add them here.
  const isProtectedResource = ["admin", "owner", "manager"].includes(
    item.role?.toLowerCase(),
  );
  return (
    <div
      key={i}
      className="flex items-center gap-2 py-3 border-b border-black/10 last:border-none"
    >
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <div className="w-[40px] h-[40px] rounded-full bg-[#F3F4F6] flex items-center justify-center shrink-0 text-[14px] font-semibold text-[#7F7F7F] overflow-hidden">
                                    {getProfileImageUrl(item) ? (
                                      <img
                                        src={getProfileImageUrl(item)}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      getInitials(item.name)
                                    )}
                                  </div>
                                  <div className="flex flex-wrap justify-between items-center gap-2 min-w-0 flex-1">
                                    <p className="text-[16px] font-normal leading-[24px] text-black/60 capitalize truncate">
                                      {item.name}
                                    </p>
                                    <span className="text-[16px] font-normal leading-[24px] text-black/60 capitalize whitespace-nowrap">
                                      {item.role
                                        ? item.role.charAt(0).toUpperCase() +
                                          item.role.slice(1).toLowerCase()
                                        : "Member"}
                                    </span>
                                  </div>
                                </div>
                              {canManage && (
                                  <div
                                    className="relative shrink-0"
                                    ref={showMenu === i ? removeMenuRef : null}
                                  >
                                    <div
                                      onClick={() =>
                                        setShowMenu(showMenu === i ? null : i)
                                      }
                                      className={`w-[20px] h-[20px] rounded flex items-center justify-center cursor-pointer ${showMenu === i ? "bg-black/10" : ""}`}
                                    >
                                      <FiMoreHorizontal
                                        className="text-black"
                                        size={16}
                                      />
                                    </div>
                                    {showMenu === i && (
                                      <div className="absolute right-[28px] top-1/2 -translate-y-1/2 h-[32px] bg-white shadow-lg rounded z-10 w-[88px] flex items-center">
                                        <button
                                          className="w-full h-full px-4 text-left text-red-500 text-sm hover:bg-gray-100 whitespace-nowrap"
                                          onClick={() => {
                                            setShowMenu(null);
                                            // Admins/owners: block with the
                                            // "cannot remove" popup instead of
                                            // the normal remove-confirmation.
                                            if (isProtectedResource) {
                                              setCannotRemoveAdmin(item);
                                            } else {
                                              setConfirmRemoveMember(item);
                                            }
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* ════ END RIGHT ════ */}
              </div>
              {/* ── END 2-COLUMN GRID ── */}

              {/* ══════════════════════════════════════════════
                  PROJECT ACTIVITY TABLE
                  Full-width below the 2-column grid (matches design)
              ══════════════════════════════════════════════ */}
              <div className="bg-white rounded-[16px] shadow-[0px_0px_4px_rgba(0,0,0,0.05)] px-3 sm:px-6 pt-4 sm:pt-6 pb-4 w-full mt-4">
                {" "}
                {/* Activity header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
                  {" "}
                  <div>
                    <h3 className="text-[24px] font-medium leading-[36px] text-black capitalize">
                      Project Activity
                    </h3>
                    <p className="text-[12px] font-normal leading-[18px] text-black/50 capitalize mt-[2px]">
                      Track tasks, resources and updates in one place
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto justify-start sm:justify-end">
                    {" "}
                    {canManage && (
                      <button
                        onClick={handleExportCSV}
                        className="w-full sm:w-[163px] h-[40px] text-[14px] font-normal leading-[21px] px-5 border border-[#4253C7] rounded-[8px] text-[#4253C7] hover:bg-blue-50 transition"
                      >
                        Export CSV
                      </button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => {
                          setTaskFormError("");
                          setShowTaskModal(true);
                        }}
                        className="w-full sm:w-[163px] h-[40px] text-[14px] font-normal leading-[21px] px-5 bg-[#4253C7] hover:bg-[#2f63e0] text-white rounded-[8px] transition"
                      >
                        Add Task
                      </button>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto -mx-6 px-6">
                  <div
                    className="grid gap-4 items-center text-sm py-4 border-b border-gray-200 font-semibold text-gray-700 min-w-[760px]"
                    style={{ gridTemplateColumns: "1fr 1.5fr 1fr 1fr 1fr 80px" }}
                  >
                    {" "}
                    <p>LIST</p>
                    <p>TASK</p>
                    <p>TYPE</p>
                    <p>PRIORITY</p>
                    <p>RESOURCE</p>
                    <p>EST.HOURS</p>
                  </div>
                  {currentTasks.map((task, index) => (
                    <div
                      key={index}
                      className="grid gap-4 items-center text-[12px] py-4 border-b border-gray-100 min-w-[760px]"
                      style={{
                        gridTemplateColumns: "1fr 1.5fr 1fr 1fr 1fr 80px",
                      }}
                    >
                      <p className="text-gray-500">{task.listName}</p>
                      <div className="flex items-center gap-3 relative">
                        <p className="text-[12px] text-[#5E5E5E]">
                          {task.name}
                        </p>
                        <div
                          className="relative"
                          ref={
                            selectedTaskDesc?.name === task.name
                              ? taskDescPopupRef
                              : null
                          }
                        >
                          <button
                            onClick={() =>
                              setSelectedTaskDesc(
                                selectedTaskDesc?.name === task.name
                                  ? null
                                  : task,
                              )
                            }
                            className="w-[22px] h-[22px] rounded-full border border-[#8A8A8A] flex items-center justify-center text-[12px] text-[#666]"
                          >
                            i
                          </button>
                          {selectedTaskDesc?.name === task.name && (
                            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-[260px] min-h-[66px] bg-white border border-[#CFCFCF] rounded-[8px] px-4 py-3 shadow-md z-20">
                              <p className="text-[12px] leading-[22px] text-[#4A4A4A]">
                                {selectedTaskDesc.description ||
                                  "No Description"}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-gray-500">{task.taskType}</p>
                      <div>
                        <span
                          className={`text-[12px] font-normal px-3 py-1 rounded-full ${getPriorityBadge(task.priority)}`}
                        >
                          {task.priority?.toUpperCase()}
                        </span>
                      </div>
                      <div
                        className="flex items-center relative"
                        onMouseEnter={() => setOpenResourceRow(index)}
                        onMouseLeave={() => setOpenResourceRow(null)}
                      >
                        <div className="flex items-center relative">
                          {(task.assignees || []).slice(0, 2).map((a, ai) => {
                            const profileImageUrl = getProfileImageUrl(a);
                            return (
                              <div
                                key={a._id}
                                className="w-[34px] h-[34px] rounded-full border-2 border-white flex items-center justify-center overflow-hidden text-[12px] font-semibold bg-[#F3F4F6] text-[#7F7F7F]"
                                style={{ marginLeft: ai === 0 ? 0 : -8 }}
                              >
                                {profileImageUrl ? (
                                  <img
                                    src={profileImageUrl}
                                    alt={a.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  getInitials(a.name)
                                )}
                              </div>
                            );
                          })}

                          {(task.assignees || []).length > 2 && (
                            <div className="w-[34px] h-[34px] rounded-full bg-[#E8E8E8] border-2 border-white flex items-center justify-center -ml-2 text-[12px] font-semibold text-[#6B7280]">
                              +{(task.assignees || []).length - 2}
                            </div>
                          )}

                          {openResourceRow === index &&
                            (task.assignees || []).length > 0 && (
                              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-[180px] min-h-[66px] bg-white border border-[#CFCFCF] rounded-[8px] px-4 py-3 shadow-md z-30 max-h-[240px] overflow-y-auto">
                                {task.assignees.map((a) => (
                                  <div
                                    key={a._id}
                                    className="flex items-center gap-3 py-1.5"
                                  >
                                    <div className="w-[32px] h-[32px] rounded-full flex items-center justify-center overflow-hidden text-[12px] font-semibold bg-[#F3F4F6] text-[#7F7F7F] shrink-0">
                                      {getProfileImageUrl(a) ? (
                                        <img
                                          src={getProfileImageUrl(a)}
                                          alt={a.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        getInitials(a.name)
                                      )}
                                    </div>
                                    <p className="text-[12px] leading-[22px] text-[#4A4A4A] truncate">
                                      {a.name}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>{" "}
                      <p className="text-gray-500 ">
                        {task.estimatedHours || 0} HOURS
                      </p>
                    </div>
                  ))}

                  {/* Pagination */}
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 pt-4 border-t border-[#E5E7EB] bg-[#F3F4F6] rounded-b-[16px] px-4 py-3">
                    <p className="text-[12px] font-medium leading-[18px] text-[#6B7280] uppercase tracking-[0.02em]">
                      {totalTaskItems === 0
                        ? "No tasks found"
                        : `Showing ${taskStartIndex + 1} To ${Math.min(taskEndIndex, totalTaskItems)} Of ${totalTaskItems} Results`}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setTaskPage((p) => Math.max(p - 1, 1))}
                        disabled={taskPage === 1}
                        className="w-[34px] h-[34px] rounded-[8px] flex items-center justify-center text-[#9CA3AF] text-lg hover:bg-[#E5E7EB] transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        ‹
                      </button>
                      {[...Array(totalTaskPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setTaskPage(i + 1)}
                          className={`w-[34px] h-[34px] rounded-[8px] text-[13px] font-medium transition flex items-center justify-center ${
                            taskPage === i + 1
                              ? "bg-[#4253C7] text-white"
                              : "text-[#374151] hover:bg-[#E5E7EB]"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() =>
                          setTaskPage((p) => Math.min(p + 1, totalTaskPages))
                        }
                        disabled={taskPage === totalTaskPages}
                        className="w-[34px] h-[34px] rounded-[8px] flex items-center justify-center text-[#9CA3AF] text-lg hover:bg-[#E5E7EB] transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* ── END ACTIVITY TABLE ── */}

              {/* Kanban button */}
              <div className="flex justify-end mt-5">
                <button
                  onClick={() => navigate(`/dashboard/kanban/${projectRef}`)}
                  className="w-full sm:w-auto bg-[#4253C7] text-white px-8 py-3 rounded-xl font-medium transition-colors"
                >
                  Open Kanban Board
                </button>
              </div>

              {/* ── CONFIRM REMOVE MEMBER MODAL ── */}
              {confirmRemoveMember && (
                <div
                  className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3"
                  onClick={() => setConfirmRemoveMember(null)}
                >
                  <div
                    className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                      Remove Member
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      Are you sure you want to remove{" "}
                      <span className="font-semibold">
                        {confirmRemoveMember.name || "this member"}
                      </span>{" "}
                      from this project?
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setConfirmRemoveMember(null)}
                        className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() =>
                          handleRemoveMember(confirmRemoveMember._id)
                        }
                        className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── CANNOT REMOVE ADMIN / MANAGER MODAL ── */}
              {cannotRemoveAdmin && (
                <div
                  className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3"
                  onClick={() => setCannotRemoveAdmin(null)}
                >
                  <div
                    className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="w-[63px] h-[63px] mx-auto rounded-full bg-[#FFF0F0] flex items-center justify-center mb-5">
                                   <AlertTriangle className="text-red-500" size={22} />

                    </div>

                    {(() => {
                      // Show role-specific copy: admins/owners get the
                      // "mandatory for every project" reasoning, managers
                      // get a simpler "cannot be removed" message.
                      const blockedRole =
                        cannotRemoveAdmin?.role?.toLowerCase();
                      const isManagerBlocked = blockedRole === "manager";
                      const roleLabel = isManagerBlocked ? "Manager" : "Admin";
                      const message = isManagerBlocked
                        ? "The project manager cannot be removed."
                        : "The project admin cannot be removed. An admin is mandatory for every project.";
                      return (
                        <>
                          <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            Cannot Remove {roleLabel}
                          </h2>
                          <p className="text-sm text-gray-600 mb-6">
                            {message}
                          </p>
                        </>
                      );
                    })()}

                    <div className="flex justify-center">
                      <button
                        onClick={() => setCannotRemoveAdmin(null)}
                        className="px-6 py-2 bg-[#4253C7] text-white rounded-lg"
                      >
                        Okay
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── CONFIRM DISABLE PROJECT MODAL ── */}
              {showDisableConfirm && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3">
                  <div className="bg-white w-full max-w-[453px] rounded-[12px] shadow-2xl px-8 py-10 text-center relative">
                    <button
                      onClick={() => setShowDisableConfirm(false)}
                      className="absolute right-5 top-5 text-black/50 hover:text-black"
                    >
                      <FiX size={22} />
                    </button>

                    <div className="w-[63px] h-[63px] mx-auto rounded-full bg-[#FFF0F0] flex items-center justify-center mb-5">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#EF4444"
                        strokeWidth="2"
                      >
                        <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                    </div>

                    <h2 className="text-[28px] leading-[42px] font-semibold text-black mb-3">
                      Disable Project?
                    </h2>
                    <p className="text-[16px] leading-[24px] text-black mb-8">
                      Disabling the project will pause all ongoing activities
                      and features. Are you sure you want to disable this
                      project?
                    </p>

                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => setShowDisableConfirm(false)}
                        className="w-[197px] h-[40px] border border-[#FF0000] rounded-[8px] text-[#FF0000] text-[14px] hover:bg-red-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleToggleDisable}
                        className="w-[197px] h-[40px] bg-[#EF4444] rounded-[8px] text-white text-[14px] hover:bg-red-600"
                      >
                        Disable
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* ── MANAGER BLOCKED POPUP ── */}
              {showManagerBlocked && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3">
                  <div className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6 text-center">
                    <p className="text-[16px] font-medium text-black mb-6">
                      Only admins can disable a project
                    </p>
                    <button
                      onClick={() => setShowManagerBlocked(false)}
                      className="px-6 py-2 bg-[#4253C7] text-white rounded-lg text-sm font-medium hover:bg-[#2f63e0]"
                    >
                      Okay
                    </button>
                  </div>
                </div>
              )}

              {/* ── ADD MEMBER MODAL ── */}
              {showModal && (
                <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 px-3">
                  <div className="bg-white w-[635px] max-w-full rounded-[12px] shadow-2xl px-[40px] py-[40px]">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-[18px]">
                      <div>
                        <h2 className="text-[28px] leading-[42px] font-semibold text-black">
                          Add New Member
                        </h2>
                        <p className="text-[16px] leading-[24px] text-black/50 mt-[8px]">
                          Add a new member and assign them to a new project
                        </p>
                      </div>
                      <button
                        onClick={() => setShowModal(false)}
                        className="text-[24px] leading-none text-black/50 hover:text-black transition mt-1"
                      >
                        ×
                      </button>
                    </div>

                    {/* Project Members label */}
                    <p className="text-[16px] leading-[24px] font-normal text-black mb-[8px]">
                      Project Members
                    </p>

                    {/* Search input — rounded full, standalone */}
                    <div className="w-full relative mb-0">
                      <FiSearch
                        className="absolute left-[14px] top-1/2 -translate-y-1/2 text-black/40"
                        size={20}
                      />
                      <input
                        type="text"
                        placeholder="Search Members"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-[40px] border border-black/20 rounded-[8px] pl-[44px] pr-3 text-[16px] text-black/50 placeholder:text-black/50 outline-none focus:border-[#4253C7]"
                      />
                    </div>

                    {/* Members list box */}
<div className="w-full bg-white border border-black/20 rounded-[8px] h-[172px] overflow-y-auto mt-[12px] mb-[32px]">
  {filteredMembers.length === 0 && (
    <div className="h-full flex items-center justify-center text-[14px] text-black/40">
      No members found
    </div>
  )}
  {filteredMembers.map((member, i) => {
                        const isExistingMember = resources.some(
                          (r) => r._id === member._id,
                        );
                        return (
                          <label
                            key={i}
                            className="flex items-center justify-between px-[20px] py-[4px] hover:bg-gray-50 cursor-pointer transition"
                          >
                            <div className="flex items-center gap-[16px]">
                              {/* Circle avatar with user icon */}
                              <div className="w-[28px] h-[28px] rounded-full border border-black flex items-center justify-center shrink-0">
                                <FiUser size={14} className="text-black" />
                              </div>
                              <span className="text-[16px] leading-[24px] text-black/50">
                                {member.name}
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={
                                isExistingMember ||
                                selectedMembers.includes(member._id)
                              }
                              disabled={isExistingMember}
                              onChange={() => {
                                if (isExistingMember) return;
                                setSelectedMembers((prev) =>
                                  prev.includes(member._id)
                                    ? prev.filter((id) => id !== member._id)
                                    : [...prev, member._id],
                                );
                              }}
                              className="w-[20px] h-[20px] accent-[#3B82F6] cursor-pointer"
                            />
                          </label>
                        );
                      })}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end gap-[12px]">
                      <button
                        onClick={() => setShowModal(false)}
                        className="w-[163px] h-[40px] text-[14px] font-normal border border-[#4253C7] rounded-[8px] text-[#4253C7] hover:bg-blue-50 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddMembers}
                        disabled={isAddingMember}
                        className="w-[163px] h-[40px] text-[14px] font-normal bg-[#4253C7] text-white rounded-[8px] hover:bg-[#2f63e0] transition"
                      >
                        {isAddingMember ? "Adding..." : "Add Member"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Projects;